// Statistics from the analytics_daily rollup (migration 20261016180000).
// All reads go through two RPCs that check access in the database: admins see
// everything, a bedemand sees its own profile, a chain owner the whole chain.
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../config/supabase'
import { useAuth } from './useAuth'

export interface SeriesRow {
  day: string
  sessions: number
  page_views: number
  searches: number
  impressions: number
  profile_clicks: number
  profile_views: number
  profile_view_sessions: number
  phone_clicks: number
  email_clicks: number
  website_clicks: number
  map_clicks: number
  listing_contacts: number
  guide_clicks: number
  position_sum: number
  position_n: number
}

export interface BreakdownRow {
  key: string
  label: string
  events: number
  sessions: number
  page_views: number
  searches: number
  impressions: number
  profile_clicks: number
  profile_views: number
  contacts: number
  guide_clicks: number
  position_sum: number
  position_n: number
}

export type Dimension =
  | 'page_type'
  | 'place'
  | 'search_place'
  | 'device'
  | 'ref'
  | 'branch'
  | 'entity'
  | 'path'
  | 'detail'
  | 'event'

export interface StatsScope {
  undertakerId?: string | null
  chainId?: string | null
}

export type SeriesTotals = Omit<SeriesRow, 'day'>

const SERIES_KEYS: (keyof SeriesTotals)[] = [
  'sessions', 'page_views', 'searches', 'impressions', 'profile_clicks', 'profile_views',
  'profile_view_sessions', 'phone_clicks', 'email_clicks', 'website_clicks', 'map_clicks',
  'listing_contacts', 'guide_clicks', 'position_sum', 'position_n',
]

/** YYYY-MM-DD in Danish time, `days` days ago. analytics_daily.day is a Copenhagen date. */
export function cphDate(daysAgo = 0, from: Date = new Date()): string {
  const d = new Date(from.getTime() - daysAgo * 86400000)
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Copenhagen' })
}

/** The selected period and the period before it, both `days` long and ending today. */
export function periodFor(days: number, today: Date = new Date()) {
  return {
    from: cphDate(days - 1, today),
    to: cphDate(0, today),
    previousFrom: cphDate(days * 2 - 1, today),
    previousTo: cphDate(days, today),
  }
}

// PostgREST returns bigint as number or string depending on size
const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v) || 0)

function normalizeSeries(rows: any[]): SeriesRow[] {
  return rows.map((r) => {
    const out: any = { day: String(r.day) }
    for (const k of SERIES_KEYS) out[k] = num(r[k])
    return out as SeriesRow
  })
}

function normalizeBreakdown(rows: any[]): BreakdownRow[] {
  return rows.map((r) => ({
    key: String(r.key),
    label: String(r.label ?? r.key),
    events: num(r.events),
    sessions: num(r.sessions),
    page_views: num(r.page_views),
    searches: num(r.searches),
    impressions: num(r.impressions),
    profile_clicks: num(r.profile_clicks),
    profile_views: num(r.profile_views),
    contacts: num(r.contacts),
    guide_clicks: num(r.guide_clicks),
    position_sum: num(r.position_sum),
    position_n: num(r.position_n),
  }))
}

export function sumSeries(rows: SeriesRow[]): SeriesTotals {
  const total = Object.fromEntries(SERIES_KEYS.map((k) => [k, 0])) as SeriesTotals
  for (const r of rows) for (const k of SERIES_KEYS) total[k] += r[k]
  return total
}

export const contactsOf = (t: Pick<SeriesTotals, 'phone_clicks' | 'email_clicks' | 'website_clicks' | 'map_clicks'>) =>
  t.phone_clicks + t.email_clicks + t.website_clicks + t.map_clicks

/** Share in percent, or null when there is nothing to divide by. */
export function rate(part: number, whole: number): number | null {
  return whole > 0 ? (part / whole) * 100 : null
}

export function avgPosition(r: { position_sum: number; position_n: number }): number | null {
  return r.position_n > 0 ? r.position_sum / r.position_n : null
}

/** Change in percent against the previous period; null when there was nothing before. */
export function trend(current: number, previous: number): number | undefined {
  if (previous === 0) return current > 0 ? undefined : 0
  return Math.round(((current - previous) / previous) * 100)
}

export const fmt = (n: number) => n.toLocaleString('da-DK')
export const fmtPct = (n: number | null, digits = 1) =>
  n === null ? '-' : `${n.toLocaleString('da-DK', { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`

/** Readable message for RPC errors, including the migration not being applied yet. */
export function statsErrorMessage(error: unknown): string {
  const e = error as { code?: string; message?: string } | null
  if (!e) return ''
  const message = e.message ?? ''
  if (e.code === '42501' || /not allowed/.test(message)) return 'Du har ikke adgang til tallene for denne profil.'
  if (e.code === 'PGRST202' || e.code === '42883') {
    return 'Statistikken er ikke sat op i databasen endnu (migration 20261016180000_analytics_rollup.sql mangler).'
  }
  return message || 'Tallene kunne ikke hentes.'
}

const scopeArgs = (scope: StatsScope) => ({
  p_undertaker_id: scope.undertakerId ?? null,
  p_chain_id: scope.chainId ?? null,
})

export function useStatsSeries(from: string, to: string, scope: StatsScope, enabled = true) {
  return useQuery({
    queryKey: ['stats', 'series', from, to, scope.undertakerId ?? null, scope.chainId ?? null],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('analytics_series', { p_from: from, p_to: to, ...scopeArgs(scope) })
      if (error) throw error
      return normalizeSeries(data || [])
    },
    enabled,
    retry: false,
  })
}

export function useStatsBreakdown(
  from: string,
  to: string,
  dim: Dimension,
  scope: StatsScope,
  limit = 25,
  enabled = true
) {
  return useQuery({
    queryKey: ['stats', 'breakdown', dim, from, to, scope.undertakerId ?? null, scope.chainId ?? null, limit],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('analytics_breakdown', {
        p_from: from,
        p_to: to,
        p_dim: dim,
        ...scopeArgs(scope),
        p_limit: limit,
      })
      if (error) throw error
      return normalizeBreakdown(data || [])
    },
    enabled,
    retry: false,
  })
}

/** One town in the area comparison. Peer values are medians of the other bedemænd
 *  shown on the same pages, null when fewer than 3 others were shown. */
export interface AreaRow {
  key: string
  label: string
  impressions: number
  profile_clicks: number
  contacts: number
  position_sum: number
  position_n: number
  peers: number
  peer_impressions: number | null
  peer_ctr: number | null
  peer_contacts: number | null
  rank_impressions: number
  rank_contacts: number
}

export function useAreaBenchmark(from: string, to: string, scope: StatsScope, enabled = true) {
  return useQuery({
    queryKey: ['stats', 'area', from, to, scope.undertakerId ?? null, scope.chainId ?? null],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('analytics_area_benchmark', { p_from: from, p_to: to, ...scopeArgs(scope) })
      if (error) throw error
      const num = (v: unknown) => (v === null || v === undefined ? null : Number(v))
      return ((data || []) as Record<string, unknown>[]).map((r) => ({
        ...(r as unknown as AreaRow),
        impressions: Number(r.impressions),
        profile_clicks: Number(r.profile_clicks),
        contacts: Number(r.contacts),
        position_sum: Number(r.position_sum),
        position_n: Number(r.position_n),
        peer_impressions: num(r.peer_impressions),
        peer_ctr: num(r.peer_ctr),
        peer_contacts: num(r.peer_contacts),
      }))
    },
    enabled,
    retry: false,
  })
}

// ---------------------------------------------------------------------------
// Which profiles the user can pick
// ---------------------------------------------------------------------------

export interface StatsEntity {
  id: string
  name: string
  type: 'undertaker' | 'chain'
}

/**
 * Admins: every chain and every bedemand outside a chain. Others: what they own,
 * by claimed_by + claim_approved or an approved claim (the same rule as the
 * database's analytics_my_*_ids()).
 */
export function useStatsEntities() {
  const { user, isAdmin, loading: authLoading } = useAuth()
  const [entities, setEntities] = useState<StatsEntity[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      setEntities([])
      setLoading(false)
      return
    }
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const list: StatsEntity[] = []
        if (isAdmin) {
          const [{ data: undertakers }, { data: chains }] = await Promise.all([
            supabase.from('undertakers').select('id, name').is('chain_id', null).order('name'),
            supabase.from('chains').select('id, name').order('name'),
          ])
          list.push(...(chains || []).map((c) => ({ id: c.id, name: c.name, type: 'chain' as const })))
          list.push(...(undertakers || []).map((u) => ({ id: u.id, name: u.name, type: 'undertaker' as const })))
        } else {
          const [{ data: undertakers }, { data: chains }, { data: claims }] = await Promise.all([
            supabase.from('undertakers').select('id, name').eq('claimed_by', user!.id).eq('claim_approved', true),
            supabase.from('chains').select('id, name').eq('claimed_by', user!.id).eq('claim_approved', true),
            supabase.from('claims').select('undertaker_id, chain_id').eq('user_id', user!.id).eq('status', 'approved'),
          ])
          const chainIds = new Set((chains || []).map((c) => c.id))
          const undertakerIds = new Set((undertakers || []).map((u) => u.id))
          const extraChains = (claims || []).map((c) => c.chain_id).filter((id): id is string => !!id && !chainIds.has(id))
          const extraUndertakers = (claims || [])
            .filter((c) => !c.chain_id)
            .map((c) => c.undertaker_id)
            .filter((id): id is string => !!id && !undertakerIds.has(id))
          const [moreChains, moreUndertakers] = await Promise.all([
            extraChains.length ? supabase.from('chains').select('id, name').in('id', extraChains) : Promise.resolve({ data: [] as { id: string; name: string }[] }),
            extraUndertakers.length ? supabase.from('undertakers').select('id, name').in('id', extraUndertakers) : Promise.resolve({ data: [] as { id: string; name: string }[] }),
          ])
          list.push(...[...(chains || []), ...(moreChains.data || [])].map((c) => ({ id: c.id, name: c.name, type: 'chain' as const })))
          list.push(...[...(undertakers || []), ...(moreUndertakers.data || [])].map((u) => ({ id: u.id, name: u.name, type: 'undertaker' as const })))
        }
        if (!cancelled) setEntities(list)
      } catch (err) {
        console.error('Error loading profiles for statistics:', err)
        if (!cancelled) setEntities([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [user, isAdmin, authLoading])

  return { entities, loading: loading || authLoading, isAdmin }
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const PAGE_TYPE_LABELS: Record<string, string> = {
  home: 'Forsiden',
  location: 'Lokationssider',
  search: 'Adressesøgning',
  profile: 'Profiler',
  guide: 'Guides',
  category: 'Viden-oversigter',
  cities: 'Byoversigten',
  static: 'Infosider',
  notfound: 'Ikke fundet (404)',
  prisberegner: 'Prisberegner',
  other: 'Andet',
}

export const DEVICE_LABELS: Record<string, string> = {
  mobile: 'Mobil',
  tablet: 'Tablet',
  desktop: 'Computer',
  unknown: 'Ukendt',
}

export function refLabel(key: string): string {
  const fixed: Record<string, string> = {
    direct: 'Direkte eller ukendt',
    search_engine: 'Søgemaskiner',
    ai: 'AI-assistenter',
    social: 'Sociale medier',
    external: 'Andre hjemmesider',
  }
  if (fixed[key]) return fixed[key]
  if (key.startsWith('internal_')) {
    const page = PAGE_TYPE_LABELS[key.slice('internal_'.length)] ?? key.slice('internal_'.length)
    return `Fra ${page.toLowerCase()} på sitet`
  }
  return key
}

export const EVENT_LABELS: Record<string, string> = {
  profile_click: '"Se profil" i lister',
  phone_reveal: 'Telefon',
  email_reveal: 'E-mail',
  website_click: 'Hjemmeside',
  map_click: 'Kort og rutevejledning',
}

export const DETAIL_LABELS: Record<string, string> = {
  'search:place': 'Valgte en by eller et postnummer',
  'search:address': 'Søgte på en adresse',
  'search:geo': 'Brugte "Min position"',
  'search:none': 'Fandt ikke adressen',
  'page_view:no_results': 'Søgning uden bedemænd i området',
  'guide_click:location': 'Guide til lokationsside',
  'guide_click:profile': 'Guide til profil',
  'guide_click:search': 'Guide til søgning',
  'guide_click:cities': 'Guide til byoversigten',
  'feed_impression:sponsored': 'Visninger som sponsoreret',
  'feed_impression:organic': 'Visninger uden sponsorat',
  'profile_click:sponsored': 'Profilklik som sponsoreret',
  'profile_click:organic': 'Profilklik uden sponsorat',
}

export function pathLabel(row: BreakdownRow): string {
  if (row.key === '/') return 'Forsiden'
  return row.label && row.label !== row.key ? row.label : row.key
}
